import { IEvent, IConsumer, createConsumer, createSubscriber } from '@cian/events-log';

import { createAsyncThunk } from '@reduxjs/toolkit';
import { selectUser } from '../../selectors/user';
import { IThunkApi } from '../../types/redux/store';
import { getUserAction } from '../user';

interface IRequestAuthenticationParameters {
  onSuccess?: Function;
  onCloseNoAuth?: Function;
}

interface IThunkApiExtended extends IThunkApi {
  rejectValue: void;
}

const TOPIC_USER = 'user';

type TConsumerSingleton = (() => IConsumer) & { _instance?: IConsumer };

const getUserConsumer: TConsumerSingleton = (): IConsumer => {
  if (!getUserConsumer._instance) {
    getUserConsumer._instance = createConsumer({ topic: TOPIC_USER });
  }

  return getUserConsumer._instance;
};

export const requestAuthentication = createAsyncThunk<void, IRequestAuthenticationParameters, IThunkApiExtended>(
  'user/requestAuthentication',
  async (parameters, { extra: context, dispatch, getState }) => {
    try {
      /* istanbul ignore else */
      if (typeof window !== 'undefined') {
        const { isAuthenticated } = selectUser(getState());

        if (isAuthenticated) {
          /* istanbul ignore else */
          if (parameters?.onSuccess) {
            parameters.onSuccess();
          }

          return;
        }

        const consumer = getUserConsumer();

        const userSubscriber = createSubscriber(async (event: IEvent<unknown>) => {
          if (event.type === 'authenticated') {
            await dispatch(getUserAction());

            if (window.__reloadHeader__) {
              window.__reloadHeader__();
            }

            if (parameters?.onSuccess) {
              parameters.onSuccess();
            }

            consumer.unsubscribe(userSubscriber);

            return;
          }

          /* istanbul ignore else */
          if (event.type === 'authentication_modal_closed') {
            /* istanbul ignore else */
            if (parameters?.onCloseNoAuth) {
              parameters.onCloseNoAuth();
            }

            consumer.unsubscribe(userSubscriber);
          }
        });

        consumer.subscribe(userSubscriber);

        if (context.microfrontendsAPI) {
          await context.microfrontendsAPI.header.openAuthModal({
            autoRefresh: false,
          });
        }
      }
    } catch (ex) {
      const { logger } = context;
      logger.error(ex, {
        domain: 'thunks/authentication/requestAuthentication',
      });

      throw ex;
    }
  },
);
